import { useState } from 'react';
import { B } from '../../constants/brand';
import { Card, Icon, useToast } from '../ui';
import { NoteBodyEditor } from '../shared/NoteBodyEditor';
import { useQuickNoteDraft, useQuickNoteLinks } from '../../notebook/QuickNote';
import { NoteLinkBar } from '../../notebook/LinkPicker';

// =============================================================
// QuickNoteWidget — the Dashboard's "Quick note" card (feedback #583).
//
// Collapsed it is one line reading "Jot a note…"; clicking it (or Expand)
// opens the same editor the Notes app and the Quick Note pop-up use, with the
// inline calculator. Saving is the pop-up's own hook (useQuickNoteDraft), so
// the note is created on the first keystroke and autosaved. It is PRIVATE
// until tagged: "+ Tag deal" links it to any deals / capital items / tasks
// (useQuickNoteLinks, the pop-up's own tag state), which shares it with them.
//
// "New note" remounts the editor under a fresh session key; the hook flushes
// the previous note's last edit as it unmounts, so nothing is lost.
// =============================================================

const btnStyle = {
  border: 0, background: 'transparent', color: B.navy, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 6px',
};

export function QuickNoteWidget({ onOpenInNotes }) {
  const [expanded, setExpanded] = useState(false);
  const [session, setSession] = useState(0);

  if (!expanded) {
    return (
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <button type="button" onClick={() => setExpanded(true)} aria-label="Write a quick note"
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
            border: 0, background: 'transparent', cursor: 'text', fontFamily: 'inherit', textAlign: 'left',
          }}>
          <Icon name="note" size={16} color={B.amber || '#c99700'} />
          <span style={{ flex: 1, fontSize: 14, color: B.textLt }}>Jot a note…</span>
          <span style={{ ...btnStyle, cursor: 'pointer' }}>
            <Icon name="chevronDown" size={14} color={B.navy} /> Expand
          </span>
        </button>
      </Card>
    );
  }

  return (
    <QuickNoteCard key={session} onOpenInNotes={onOpenInNotes}
      onNew={() => setSession(s => s + 1)} onCollapse={() => setExpanded(false)} />
  );
}

function QuickNoteCard({ onOpenInNotes, onNew, onCollapse }) {
  const { toast } = useToast();
  const tags = useQuickNoteLinks({ toast });
  const draft = useQuickNoteDraft({ toast, getLinks: tags.getLinks });
  tags.bind(draft);
  const { noteId, created, state, onChange } = draft;

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${B.divider || B.border}` }}>
        <Icon name="note" size={16} color={B.amber || '#c99700'} />
        <strong style={{ fontSize: 13, color: B.text }}>Quick note</strong>
        <span style={{ fontSize: 11, color: state === 'error' ? B.red : B.textLt }}>
          {{ empty: 'Private', saving: 'Saving…', saved: tags.links.length ? 'Saved' : 'Saved · private', error: 'Not saved' }[state]}
        </span>
        <span style={{ flex: 1 }} />
        {created && onOpenInNotes && (
          <button type="button" style={btnStyle} onClick={() => onOpenInNotes(noteId)}>Open in Notes</button>
        )}
        {created && (
          <button type="button" style={btnStyle} onClick={onNew}>
            <Icon name="add" size={13} color={B.navy} /> New note
          </button>
        )}
        <button type="button" style={btnStyle} onClick={onCollapse} aria-label="Collapse quick note">
          <Icon name="chevronUp" size={14} color={B.navy} /> Collapse
        </button>
      </div>
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px 0' }}>
        <NoteBodyEditor onChange={onChange} minHeight={180} autoFocus mathAssist
          style={{ border: 0, background: 'transparent' }} />
      </div>
      <NoteLinkBar links={tags.links} busy={tags.busy} onAdd={tags.add} onRemove={tags.remove} />
    </Card>
  );
}
